import { useState, useEffect, useContext } from "react";
import Select from "../../../../../components/Select";
import { Link } from "react-router-dom";
import GenericInputDate from "../../../../../components/GenericInputDate";
import { OutorgaContext } from "../../../../../contexts/Outorga";
import { getMonitoradosByCliente } from "../../../../../services/monitorados";
import { getClientesModulo } from "../../../../../services/clientes";
import auth from "../../../../../utils/auth";
import { normalizeToTimestamp, toIsoDateWithoutTime } from "../../../../../utils/normalizeDateHelpers";
import GenericModal from "../../../../../components/GenericModal";
import ReactLoading from "react-loading";
import helper from "../../../../../utils/helper";

const BasicForm = (props) => {
    const { permissions = {}, isMasterOrAdmin } = props
    const context = useContext(OutorgaContext)
    const { grantId } = props

    const [details, setDetails] = useState({
        description: "",
        client: null,
        monitored: null,
        captation: null,
        referenceId: "",
        validity: null
    })
    const [clients, setClients] = useState([])
    const [monitoreds, setMonitoreds] = useState([])
    const [captations, setCaptations] = useState([])
    const [grantData, setGrantData] = useState(null)
    const [modalInfo, setModalInfo] = useState({
        isOpen: false,
        title: "",
        description: "",
        grantId: null
    })

    const isCliente = auth.getUserInfo()?.cliente || props.isCliente || false
    const {
        isLoadingGrant,
        hasUnsavedBasicInfo,
        unsavedBasicInfo,
        captationsList,
        getGrant,
        updateGrant,
        createGrant
    } = context

    const canCreate = permissions.post
    const canEdit = permissions.patch
    const canSaveGrant = isMasterOrAdmin || canCreate || canEdit

    useEffect(() => {
        loadInitialData()
    }, [])

    useEffect(() => {
        if (!grantData) return
        if (!captations.length) return
        if (!isCliente && !clients.length) return

        loadToEdit(grantData)
    }, [grantData, clients, captations])

    useEffect(() => {
        const payload = {
            nome: details?.description,
            captacao: details?.captation,
            monitorado: details?.monitored,
            id_referencia: details?.referenceId,
            data_inicio: details?.validity?.startDate ? toIsoDateWithoutTime(details?.validity?.startDate, true) : null,
            data_vencimento: details?.validity?.endDate ? toIsoDateWithoutTime(details?.validity?.endDate, true) : null
        }

        if(Object.values(payload).some(value => value ?? false)) unsavedBasicInfo(payload)
    }, [details])

    const loadInitialData = async () => {
        try {
            const user = auth.getUserInfo()

            setCaptations(captationsList || [])

            if (isCliente) {
                const clientId = user?.cliente

                setDetails(prev => ({
                    ...prev,
                    client: clientId
                }))

                await fetchMonitorados(clientId)
            } else {
                const clientsResponse = await getClientesModulo("ambiental")

                const clientsMapped = clientsResponse.map((client) => ({
                    value: client.cliente__id,
                    label: client.cliente__nome_fantasia || ""
                }))

                setClients(clientsMapped)
            }

            if (grantId) {
                const res = await getGrant(grantId)

                if (res?.status === 200) {
                    setGrantData(res.data)
                }
            }

        } catch (error) {
            console.error("Erro ao carregar dados:", error)
            setClients([])
            setCaptations([])
            setMonitoreds([])
        }
    }

    const fetchMonitorados = async (clientId) => {
        if (!clientId) return

        try {
            const monitorados = await getMonitoradosByCliente(clientId)

            const monitoredsOptions = monitorados.map((m) => ({
                value: m.value,
                label: m.label || ""
            }))

            setMonitoreds(monitoredsOptions)
        } catch (error) {
            console.error("Erro ao buscar monitorados:", error)
            setMonitoreds([])
        }

    }

    const loadToEdit = async (basicInfo) => {
        try {
            const monitorados = await getMonitoradosByCliente(basicInfo.cliente)

            const monitoredsOptions = monitorados.map((m) => ({
                value: m.value,
                label: m.label || ""
            }))

            setMonitoreds(monitoredsOptions)

            setDetails({
                description: basicInfo.nome || "",
                client: basicInfo.cliente,
                monitored: basicInfo.monitorado,
                captation: basicInfo.captacao,
                referenceId: basicInfo.id_referencia || "",
                validity: normalizeToTimestamp(
                    basicInfo.data_inicio,
                    basicInfo.data_vencimento
                )
            })

        } catch (error) {
            console.error("Erro ao carregar edição:", error)
        }

    }

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target

        setDetails(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }))

    }

    const handleChangeSelect = async (option, field) => {
        setDetails(prev => ({
            ...prev,
            [field]: option.value || null
        }))

        if (field === "client") {
            setMonitoreds([])

            setDetails(prev => ({
                ...prev,
                monitored: null,
                client: option.value
            }))

            if (!option?.value) return

            await fetchMonitorados(option.value)
        }
    }

    const saveAction = async () => {
        try {
            if (grantId) {
                await updateGrant(grantId, details)

                setModalInfo({
                    isOpen: true,
                    title: "Outorga atualizada",
                    description: "A outorga foi atualizada com sucesso."
                })
            } else {
                const res = await createGrant(details)

                setModalInfo({
                    isOpen: true,
                    title: "Outorga cadastrada",
                    description: "A outorga foi cadastrada com sucesso.",
                    grantId: res.data.id
                })
            }
        } catch (error) {
            console.log(error)
        }
    }

    const canSave = () => {
        const { description, monitored, captation, validity } = details

        return !!(
            description?.trim() &&
            monitored &&
            captation &&
            validity &&
            Object.keys(validity).length > 0
        )
    }

    const resetModal = () => {
        setModalInfo({
            isOpen: false,
            title: "",
            description: ""
        })

        if (!grantId) {
            window.location.href = `/ambiental/outorga/${modalInfo.grantId}/editar`

            setTimeout(() => {
                window.location.reload()
            }, 500)
        }
    }

    return (
        <div className="hdv-form-default" style={{ position: "relative", minHeight: "370px" }}>
            {isLoadingGrant && (
                <div className="loading-screen-overlay">
                    <ReactLoading type="spin" color="var(--background-menu)" width={40} />
                </div>
            )}

            <div className="row">
                <div className="col-md-12">
                    <label className="required">Descrição *</label>
                    <input
                        type="text"
                        name="description"
                        value={details.description}
                        onChange={handleChange}
                    />
                </div>
            </div>

            <div className="row">
                {!isCliente && (
                    <div className="col-md-4">
                        <label className="required">Cliente *</label>
                        <Select
                            value={details.client}
                            onChange={(e) => handleChangeSelect(e, "client")}
                            options={clients}
                            resolveValueFromOptions
                        />
                    </div>
                )}

                <div className={!isCliente ? "col-md-4" : "col-md-6"}>
                    <label className="required">Monitorado *</label>
                    <Select
                        value={details.monitored}
                        onChange={(e) => handleChangeSelect(e, "monitored")}
                        options={monitoreds}
                        resolveValueFromOptions
                    />
                </div>

                <div className={!isCliente ? "col-md-4" : "col-md-6"}>
                    <label className="required">Captação *</label>
                    <Select
                        value={details.captation}
                        onChange={(e) => handleChangeSelect(e, "captation")}
                        options={captations}
                        resolveValueFromOptions
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-md-6">
                    <label>Id de Referência</label>
                    <input
                        type="text"
                        name="referenceId"
                        value={details.referenceId}
                        onChange={handleChange}
                    />
                </div>

                <div className="col-md-6">
                    <label className="required">Validade da Outorga *</label>
                    <GenericInputDate
                        format="timestamp_seconds"
                        value={details.validity}
                        onChange={(value) =>
                            setDetails(prev => ({ ...prev, validity: value }))
                        }
                        disableMaxDate={true}
                    />
                </div>
            </div>

            <div className="hdv-btn-group">
                {
                    canSaveGrant && (
                        <button
                            onClick={saveAction}
                            className="hdv-btn-forms hdv-btn-green"
                            disabled={!canSave() || isLoadingGrant}
                        >
                            Salvar
                        </button>
                    )
                }

                <Link
                    to="/ambiental/outorga"
                    onClick={(e) => {
                        if (hasUnsavedBasicInfo()) {
                            e.preventDefault()

                            helper.dispatchEvent("showAviso", {
                                message: "Existem informações da outorga não salvas! Deseja sair?",
                                callback: () => {
                                    props.history.push("/ambiental/outorga")
                                }
                            })
                        }
                    }}
                >
                    <button className="hdv-btn-forms hdv-btn-orange">
                        Cancelar
                    </button>
                </Link>
            </div>

            <GenericModal
                isOpen={modalInfo.isOpen}
                title={modalInfo.title}
                description={modalInfo.description}
                onClose={resetModal}
                onConfirm={() => props.history.push("/ambiental/outorga")}
            />
        </div>
    )
}

export default BasicForm